import { motion, useReducedMotion } from "framer-motion";
import { Reveal, SectionTag, EASE, MaskedLines } from "@/components/Reveal";
import { GoldDot } from "@/components/InsightVisuals";

const PRINCIPLES = [
  { node: "Solve", num: "01", name: "Solve Real Problems", desc: "Start with a genuine customer or business requirement." },
  { node: "Structure", num: "02", name: "Build Structured Systems", desc: "Create processes that can be understood, managed and improved." },
  { node: "Customer", num: "03", name: "Keep the Customer at the Centre", desc: "Technology and operations should ultimately improve the customer experience." },
  { node: "Technology", num: "04", name: "Use Technology with Purpose", desc: "Implement technology where it improves access, efficiency, visibility or execution." },
  { node: "Scale", num: "05", name: "Build for Sustainable Scale", desc: "Strengthen the model before increasing complexity." },
];

/* pentagonal principle architecture: centre concept + five nodes */
const PrinciplesWheel = ({ reduce }) => {
  const CXp = 210;
  const CYp = 210;
  const RAD = 138;
  const pts = PRINCIPLES.map((_, i) => {
    const a = ((-90 + i * 72) * Math.PI) / 180;
    return [CXp + RAD * Math.cos(a), CYp + RAD * Math.sin(a)];
  });
  return (
    <div data-testid="principles-wheel" className="relative" aria-hidden="true">
      <div
        className="pointer-events-none absolute left-1/2 top-1/2 h-[70%] w-[80%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#D4AF37]/[0.07] blur-[100px]"
        aria-hidden="true"
      />
      <svg viewBox="0 0 420 420" className="relative mx-auto w-full max-w-sm">
        <motion.circle
          cx={CXp} cy={CYp} r={RAD} fill="none"
          stroke="rgba(212,175,55,0.18)" strokeWidth="1" strokeDasharray="2 6"
          initial={reduce ? {} : { opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1.6, ease: EASE, delay: 0.3 }}
        />
        {pts.map((p, i) => (
          <motion.line
            key={i}
            x1={CXp} y1={CYp} x2={p[0]} y2={p[1]}
            stroke="rgba(212,175,55,0.35)" strokeWidth="1"
            initial={reduce ? {} : { pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1.2, ease: EASE, delay: 0.5 + i * 0.2 }}
          />
        ))}
        <motion.rect
          x={CXp - 8} y={CYp - 8} width="16" height="16"
          transform={`rotate(45 ${CXp} ${CYp})`}
          fill="rgba(212,175,55,0.55)"
          initial={reduce ? {} : { opacity: 0, scale: 0.3 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.2 }}
          style={{ transformOrigin: `${CXp}px ${CYp}px` }}
        />
        <motion.text
          x={CXp} y={CYp + 28} textAnchor="middle" fontSize="9" letterSpacing="2.5" fill="#E7CE8B"
          style={{ textTransform: "uppercase", fontFamily: "'Fraunces Variable', Georgia, serif" }}
          initial={reduce ? {} : { opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.6 }}
        >
          How We Think
        </motion.text>
        {PRINCIPLES.map((p, i) => (
          <g key={p.num}>
            <GoldDot cx={pts[i][0]} cy={pts[i][1]} r={4.5} delay={0.8 + i * 0.2} reduce={reduce} />
            <motion.text
              x={pts[i][0]} y={pts[i][1] - 16} textAnchor="middle" fontSize="8.5" letterSpacing="2"
              fill="#C9C2B4"
              style={{ textTransform: "uppercase", fontFamily: "'Fraunces Variable', Georgia, serif" }}
              initial={reduce ? {} : { opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.95 + i * 0.2 }}
            >
              {p.node}
            </motion.text>
            <motion.text
              x={pts[i][0]} y={pts[i][1] + 22} textAnchor="middle" fontSize="20"
              fill="rgba(255,255,255,0.07)"
              style={{ fontFamily: "'Fraunces Variable', Georgia, serif" }}
              initial={reduce ? {} : { opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, ease: EASE, delay: 1.05 + i * 0.2 }}
            >
              {p.num}
            </motion.text>
          </g>
        ))}
      </svg>
    </div>
  );
};

export default function Principles() {
  const reduce = useReducedMotion();
  return (
    <section
      id="principles"
      data-testid="principles-section"
      className="relative overflow-hidden border-t border-white/5 bg-[#100F0B] py-20 sm:py-24"
    >
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <SectionTag index="13">How We Think</SectionTag>
        </Reveal>
        <h2
          data-testid="principles-headline"
          className="mt-6 font-serif text-3xl font-normal leading-[1.12] tracking-tight text-slate-100 sm:text-4xl lg:text-[44px]/[1.14]"
        >
          <MaskedLines delay={0.1}>
            Principles behind
            <br />
            <span className="italic text-transparent bg-gradient-to-r from-[#F0DDA6] via-[#E7CE8B] to-[#C9A227] bg-clip-text">
              the way we build.
            </span>
          </MaskedLines>
        </h2>
        <Reveal delay={0.2}>
          <p className="mt-7 max-w-xl text-base font-normal leading-relaxed text-stone-400 sm:text-lg">
            Technology matters, but the way a business thinks about the
            customer, the problem and the operating system matters just as
            much.
          </p>
        </Reveal>

        <div className="mt-14 grid items-center gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
          <Reveal delay={0.15}>
            <PrinciplesWheel reduce={reduce} />
          </Reveal>
          <div data-testid="principles-list" className="border-t border-white/10">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.num} delay={0.08 * i}>
                <div
                  data-testid={`principle-${i + 1}`}
                  className="group grid gap-2 border-b border-white/10 py-6 transition-colors duration-500 hover:border-[#D4AF37]/40 sm:grid-cols-[56px_1fr] sm:gap-6"
                >
                  <span className="text-xs font-medium tracking-[0.25em] text-[#D4AF37]/80">{p.num}</span>
                  <span>
                    <span className="block text-xs font-semibold uppercase tracking-[0.22em] text-slate-100 transition-colors duration-500 group-hover:text-[#E7CE8B]">
                      {p.name}
                    </span>
                    <span className="mt-2 block text-sm font-normal leading-relaxed text-stone-400">
                      {p.desc}
                    </span>
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* transition into Section 14: how we think -> how we work */}
        <div className="mt-16 flex flex-col items-center" aria-hidden="true">
          <motion.p
            className="text-[10px] font-medium uppercase tracking-[0.32em] text-stone-500"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 1, ease: EASE }}
          >
            How We Think &nbsp;→&nbsp; How We Work
          </motion.p>
          <motion.span
            className="mt-5 h-12 w-px origin-top bg-gradient-to-b from-[#D4AF37]/50 to-[#D4AF37]/10"
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 1.3, ease: EASE, delay: 0.2 }}
          />
          <motion.span
            className="mt-[-1px] h-[8px] w-[8px] rotate-45 border border-[#D4AF37]/60 bg-[#100F0B]"
            initial={{ opacity: 0, scale: 0.3 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.6 }}
          />
        </div>
      </div>
    </section>
  );
}
